import { HTMLAttributes } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div<{ short?: boolean }>`
    position: relative;
    max-height: ${(props) => (props.short ? '6rem' : 'inherit')};
    overflow: ${(props) => (props.short ? 'hidden' : 'inherit')};
`;

const Text = styled.div<{ short?: boolean }>`
    ul,
    ol {
        margin: 2rem;
        padding: 0;
    }

    ol li[data-list='bullet'] {
        list-style-type: disc;
    }

    ol li[data-list='ordered'] {
        list-style-type: decimal;
    }

    p {
        margin-bottom: 0.25rem;
    }

    .ql-ui {
        display: none;
    }
`;

const OverflowBorder = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4rem;
    box-shadow: white 0px -40px 13px -1rem inset;
`;

interface Props extends HTMLAttributes<HTMLDivElement> {
    children: string;
    short?: boolean;
    overflowBorder?: boolean;
}

// Adds target="_blank" + rel="noopener noreferrer" to every anchor so WYSIWYG links open safely in a new tab.
const withExternalLinks = (html: string): string =>
    (html ?? '').replace(/<a\b([^>]*)>/gi, (_match, attrs: string) => {
        let nextAttrs = attrs;
        if (!/\btarget\s*=/i.test(nextAttrs)) nextAttrs += ' target="_blank"';
        if (!/\brel\s*=/i.test(nextAttrs)) nextAttrs += ' rel="noopener noreferrer"';
        return `<a${nextAttrs}>`;
    });

// Renders stored WYSIWYG HTML, forcing links to open in a new tab.
export const WYSIWYGText = ({ children, overflowBorder, ...rest }: Props) => {
    return (
        <Wrapper {...rest}>
            <Text
                dangerouslySetInnerHTML={{
                    __html: withExternalLinks(children)
                }}
            />
            {overflowBorder && <OverflowBorder />}
        </Wrapper>
    );
};

export { Text as WYSIWYGTextStyle };

// Returns true when WYSIWYG HTML has no visible text and no embedded media (e.g. empty Quill output like <p><br></p>).
export const isWysiwygEmpty = (html?: string | null): boolean => {
    if (!html) return true;
    if (/<(img|iframe)\b/i.test(html)) return false;
    const text = html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;|&#160;/gi, ' ')
        .trim();
    return text.length === 0;
};
